import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import { get } from 'lodash'
import {
  Box,
  Button,
  Grid,
  TextField,
  makeStyles,
  Typography,
  Snackbar,
  Container,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import {
  AlternateEmail,
  AttachFile,
  Clear,
  Person,
} from '@material-ui/icons'

const useStyles = makeStyles(({ palette }) => ({
  wrapper: {
    color: palette.primary.contrastText,
    backgroundColor: palette.background.paper,
  },
  inputWithoutFile: {
    color: 'transparent',
    '&::-webkit-file-upload-button': {
      visibility: 'hidden',
      background: 'transparent',
      border: 0,
      margin: 0,
      padding: 0,
      width: 0,
    },
  },
  inputWithFile: {
    color: 'currentcolor',
  },
  icon: {
    cursor: 'pointer',
  },
}))

const NAME = 'name'
const EMAIL = 'email'
const RESUME = 'resume'
const COVER_LETTER = 'coverLetter'

const initialInputs = {
  [NAME]: '',
  [EMAIL]: '',
  [RESUME]: '',
  [COVER_LETTER]: '',
}
const initialAlert = { open: false, message: '' }

const emailRegex = /\S+@\S+\.\S+/
const isPresent = (x) =>
  typeof x === 'string' && Boolean(x.trim())

const isEmail = (x) =>
  typeof x === 'string' && emailRegex.test(x)

const validations = {
  [NAME]: isPresent,
  [EMAIL]: isEmail,
}

const validateForm = (spec, obj) =>
  Object.keys(spec).reduce(
    (acc, key) =>
      spec[key](obj[key])
        ? acc
        : { ...acc, [key]: `${key} is not valid` },
    {}
  )

const InputWrapper = ({ children }) => (
  <Grid item xs={12} sm={12} md={6} lg={6}>
    {children}
  </Grid>
)

InputWrapper.propTypes = {
  children: PropTypes.node.isRequired,
}

const JobForm = ({ job }) => {
  const [inputs, setInputs] = React.useState(initialInputs)
  const [errors, setErrors] = React.useState(null)
  const [alert, setAlert] = React.useState(initialAlert)
  const { t } = useTranslation()
  const cls = useStyles()

  const formRef = React.createRef()

  const getFileInputClassName = (file) => {
    return file
      ? [cls.inputWithoutFile, cls.inputWithFile].join(' ')
      : cls.inputWithoutFile
  }

  const handleSubmit = (submitResume) => (e) => {
    e.preventDefault()
    const result = validateForm(validations, inputs)
    return !Object.keys(result).length
      ? submitResume()
      : setErrors(result)
  }

  const handleChange = (e) => {
    e.persist()
    const { name } = e.target
    setInputs((cur) => ({
      ...cur,
      [name]: e.target.value,
    }))
  }

  const handleClearFile = (name) => () =>
    setInputs((cur) => ({
      ...cur,
      [name]: '',
    }))

  const submitResume = () => {
    setErrors(null)
    const formData = new FormData(formRef.current)
    formData.append('jobTitle', job)

    return axios
      .post('/.netlify/functions/newApplication', formData)
      .then(() => {
        setInputs(initialInputs)
        setAlert({
          open: true,
          message: t('descriptions:resumeSubmission'),
          severity: 'success',
        })
        setTimeout(() => setAlert(initialAlert), 3000)
      })
      .catch(() => {
        setAlert({
          open: true,
          message: t('descriptions:resumeSubmissionFail'),
          severity: 'error',
        })

        setTimeout(() => setAlert(initialAlert), 3000)
      })
  }

  const handleError = (name) => get(errors, name, '')

  const generateInputProps = (type) => ({
    value: inputs[type],
    onChange: handleChange,
    label: t(`labels:${type}`),
    variant: 'filled',
    name: type,
    fullWidth: true,
    id: type,
  })

  const generateErrorProps = (type) => ({
    error: !!handleError(type),
    helperText: handleError(type),
  })

  const generateFileInputProps = (type) => ({
    inputProps: {
      className: getFileInputClassName(inputs[type]),
      accept: '.pdf,.doc,.docx',
    },
    InputProps: {
      endAdornment: inputs[type] ? (
        <Clear
          className={cls.icon}
          onClick={handleClearFile(type)}
        />
      ) : (
        <AttachFile />
      ),
    },
  })

  return (
    <Box p={5} className={cls.wrapper}>
      <Container>
        <Typography variant="h3" color="inherit">
          {t('titles:submitYourApplication')}
        </Typography>
        <Grid
          container
          spacing={3}
          component="form"
          onSubmit={handleSubmit(submitResume)}
          ref={formRef}
        >
          <InputWrapper>
            <TextField
              type="text"
              {...generateInputProps(NAME)}
              {...generateErrorProps(NAME)}
              InputProps={{
                endAdornment: <Person />,
              }}
              required
            />
          </InputWrapper>
          <InputWrapper>
            <TextField
              type="email"
              {...generateInputProps(EMAIL)}
              {...generateErrorProps(EMAIL)}
              InputProps={{
                endAdornment: <AlternateEmail />,
              }}
              required
            />
          </InputWrapper>
          <InputWrapper>
            <TextField
              type="file"
              required
              {...generateInputProps(RESUME)}
              {...generateErrorProps(RESUME)}
              {...generateFileInputProps(RESUME)}
            />
          </InputWrapper>
          <InputWrapper>
            <TextField
              type="file"
              {...generateInputProps(COVER_LETTER)}
              {...generateFileInputProps(COVER_LETTER)}
            />
          </InputWrapper>
          <Grid item>
            <Button
              color="secondary"
              variant="contained"
              size="large"
              type="submit"
              disabled={alert.open}
            >
              {t('labels:apply')}
            </Button>
          </Grid>
        </Grid>
        <Snackbar
          open={alert.open}
          anchorOrigin={{
            horizontal: 'right',
            vertical: 'top',
          }}
        >
          <Alert severity={alert.severity}>
            {alert.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  )
}

JobForm.propTypes = {
  job: PropTypes.string.isRequired,
}

export default JobForm
